import { useEffect, useState } from 'react';

import {
  ChakraFlex,
  FAIconName,
  Text,
  useToastMessage,
} from '@equip.health/ui';

import {
  Address,
  OrderSteps,
  stepTitleMap,
  UserRole,
} from '~/lib/constants/patients.constants';
import {
  OrderBodyTraceRequest,
  useAddBodyTraceOrdersMutation,
  useAddBodyTraceReorderMutation,
  useGetSupportDetailsBySupportExternalIdQuery,
} from '~/store/services/clinicalData';

import {
  isAllAddressFieldsNotEmpty,
  isReorderApiReason,
} from '../util/clinicalProfile.util';
import useEquipScaleWarnings from './useEquipScaleWarnings';

const useOrderEquipScale = (
  patientId: string,
  age: string,
  patient: Patient,
  onClose?: () => void,
  isReorder?: boolean,
  reorderReasonCode?: string,
) => {
  const {
    firstName,
    lastName,
    chosenName,
    addressLine1,
    addressLine2,
    city,
    state,
    zip,
    email,
    phoneNumber,
    shippingAddress,
    supports,
  } = patient;
  const TOP_LEVEL_ID = 'order-equip-scale';
  const [currentWarning, setCurrentWarning] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<OrderSteps>();
  const [addressData, setAddressData] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);

  const { warnings } = useEquipScaleWarnings(patientId, Number(age));

  const [orderBodyTrace, orderBodyTraceResult] =
    useAddBodyTraceOrdersMutation();
  const [reorderBodyTrace, reorderBodyTraceResult] =
    useAddBodyTraceReorderMutation();

  const shouldUseReorderApi =
    isReorder === true &&
    reorderReasonCode != null &&
    isReorderApiReason(reorderReasonCode);

  const orderBodyTraceError = shouldUseReorderApi
    ? reorderBodyTraceResult.isError
    : orderBodyTraceResult.isError;

  const orderBodyTraceLoading = shouldUseReorderApi
    ? reorderBodyTraceResult.isLoading
    : orderBodyTraceResult.isLoading;

  const { createToast } = useToastMessage();
  const isProxy = supports?.some((support) => support.isProxy) ?? false;
  const supportExternalId = supports?.find(
    (support) => support.isProxy,
  )?.externalId;

  const { data: supportDetails } = useGetSupportDetailsBySupportExternalIdQuery(
    supportExternalId,
    {
      skip: !isProxy,
    },
  );

  const initializeState = () => {
    if (addressData.length === 0) {
      return;
    }

    const allAddressesValid = addressData.every(isAllAddressFieldsNotEmpty);

    if (isReorder) {
      setCurrentStep(
        allAddressesValid
          ? OrderSteps.SHIPPING_ADDRESS
          : OrderSteps.ENTER_MANUAL_ADDRESS,
      );
      return;
    }

    if (warnings.length > 0) {
      setCurrentStep(OrderSteps.WARNING);
      return;
    }

    setCurrentStep(
      allAddressesValid
        ? OrderSteps.SHIPPING_ADDRESS
        : OrderSteps.ENTER_MANUAL_ADDRESS,
    );
  };

  useEffect(() => {
    initializeState();
  }, [warnings, addressData]);

  useEffect(() => {
    const initialAddress = {
      name: `${chosenName || firstName} ${lastName}`,
      addressLine1: shippingAddress?.addressLine1 || addressLine1,
      addressLine2: shippingAddress?.addressLine2 || addressLine2,
      city: shippingAddress?.city || city,
      state: shippingAddress?.state || state,
      zipCode: shippingAddress?.zip || zip,
      phoneNumber,
      email,
      userRole: UserRole.PATIENT,
      firstName: chosenName || firstName,
      lastName,
    };

    let updatedAddressData = [initialAddress];

    if (supportDetails) {
      const supportAddress = {
        name: `${supportDetails?.chosenName || supportDetails?.firstName} ${supportDetails?.lastName}`,
        addressLine1: supportDetails?.address?.addressLine1 || '',
        addressLine2: supportDetails?.address?.addressLine2 || '',
        city: supportDetails?.address?.city || '',
        state: supportDetails?.address?.state || '',
        zipCode: supportDetails?.address?.zip || '',
        phoneNumber: supportDetails?.cellPhone || '',
        email: supportDetails?.email || '',
        userRole: UserRole.PROXY,
        firstName: supportDetails?.chosenName || supportDetails?.firstName,
        lastName: supportDetails?.lastName,
      };

      const isInitialAddressValid = isAllAddressFieldsNotEmpty(initialAddress);
      const isSupportAddressValid = isAllAddressFieldsNotEmpty(supportAddress);

      if (isInitialAddressValid && isSupportAddressValid) {
        updatedAddressData = [supportAddress, initialAddress];
      } else if (isInitialAddressValid && !isSupportAddressValid) {
        updatedAddressData = [initialAddress];
      } else if (!isInitialAddressValid && isSupportAddressValid) {
        updatedAddressData = [supportAddress];
      } else {
        updatedAddressData = [initialAddress];
      }
    } else if (isAllAddressFieldsNotEmpty(initialAddress)) {
      updatedAddressData = [initialAddress];
    }

    setAddressData(updatedAddressData);
  }, [
    firstName,
    lastName,
    addressLine1,
    addressLine2,
    city,
    state,
    zip,
    email,
    phoneNumber,
    shippingAddress,
    supportDetails,
    isProxy,
  ]);

  const handleProceedWithOrder = () => {
    const allAddressesValid = addressData.every(isAllAddressFieldsNotEmpty);
    if (currentWarning < warnings.length - 1) {
      setCurrentWarning(currentWarning + 1);
    } else if (!allAddressesValid) {
      setCurrentStep(OrderSteps.ENTER_MANUAL_ADDRESS);
    } else {
      setCurrentStep(OrderSteps.SHIPPING_ADDRESS);
    }
  };

  const handleConfirmOrder = () => {
    const payload: OrderBodyTraceRequest = {
      addressEmail: selectedAddress.email,
      addressFirstName: selectedAddress.firstName,
      addressLastName: selectedAddress.lastName,
      addressPhoneNumber: selectedAddress.phoneNumber.replace(/\D/g, ''),
      patientExternalId: patientId,
      addressTypeLabel: selectedAddress.userRole,
      shipToAddressId: null,
      shipToAddress: {
        city: selectedAddress.city,
        country: 'US',
        county: null,
        postalCode: selectedAddress.zipCode,
        state: selectedAddress.state,
        street: selectedAddress.addressLine1,
        street2: selectedAddress.addressLine2 || null,
      },
    };

    const mutate = shouldUseReorderApi
      ? () =>
          reorderBodyTrace({ ...payload, reorderReason: reorderReasonCode! })
      : () => orderBodyTrace(payload);

    mutate()
      .unwrap()
      .then(() => {
        createToast({
          icon: FAIconName.checkRegular,
          id: `${TOP_LEVEL_ID}__success-toast`,
          text: 'Order successfully placed.',
          variant: 'success',
        });
        onClose?.();
      })
      .catch(() => {});
  };

  const handleAddressSelection = (address: Address) => {
    setSelectedAddress(address);
    setCurrentStep(OrderSteps.REVIEW_ORDER);
  };

  const modalTitle = (() => {
    switch (currentStep) {
      case OrderSteps.WARNING:
        if (warnings.length > 0) {
          return warnings[currentWarning].title;
        }
      case OrderSteps.SHIPPING_ADDRESS:
        return (
          <ChakraFlex flexDirection="column" gap="12px" marginBottom="6px">
            <Text color="neutral.primary" variant="h4">
              {stepTitleMap[OrderSteps.SHIPPING_ADDRESS]}
            </Text>
            <Text color="neutral.primary" variant="body">
              To update an existing address, please edit the patient profile.
            </Text>
          </ChakraFlex>
        );
      case OrderSteps.REVIEW_ORDER:
        return (
          <ChakraFlex flexDirection="column" gap="12px" marginBottom="6px">
            <Text color="neutral.primary" variant="h4">
              {stepTitleMap[OrderSteps.REVIEW_ORDER]}
            </Text>
            <Text color="neutral.primary" variant="body">
              Please review and confirm the details below before placing the
              order.
            </Text>
          </ChakraFlex>
        );
      default:
        return stepTitleMap[currentStep];
    }
  })();

  return {
    addressData,
    currentStep,
    currentWarning,
    handleProceedWithOrder,
    modalTitle,
    isAllAddressFieldsNotEmpty,
    handleConfirmOrder,
    orderBodyTraceError,
    orderBodyTraceLoading,
    selectedAddress,
    initializeState,
    handleAddressSelection,
    setCurrentStep,
    warnings,
  };
};

export default useOrderEquipScale;
